/**
 * useSite Hook
 *
 * This hook contains logic to handle what "site" is active (only "default" available - can add others if needed).
 * Each site may have distinct config.
 * This hook streamlines access to this config.
 *
 * This hook may be called from React components and hooks
 *
 * Use to access:
 * * Current active siteId - statically set to "default"
 * * Current site data - loaded from local JSON file
 * * Site URLs - based on the current browser URL
 */

import { useCallback } from 'react';

import sitesData from './site.json';

export type SiteId = string;

export interface SiteData {
  defaultCart: string,
  campaign: {
    name: string,
    transSource: string,
    transType: string,
  },
}

export type SitesData = Record<SiteId, SiteData>;

export interface SiteUrls {
  transSourceUrl: string,
}

const DEFAULT_SITE: keyof SitesData = 'default';

const loadSiteData = (siteId: keyof SitesData): SiteData => {
  if (!(siteId in sitesData)) {
    throw new Error(`Site ${siteId} does not exist`);
  }

  return sitesData[siteId as keyof typeof sitesData];
};

export function useSite() {
  const getSiteId = useCallback((): keyof SitesData => (
    DEFAULT_SITE
  ), []);

  const getSiteData = useCallback((): SiteData => {
    const siteId = getSiteId();
    return loadSiteData(siteId);
  }, [getSiteId]);

  const getBaseUrl = useCallback((): string => (
    `${window.location.protocol}//${window.location.host}/`
  ), []);

  const getSiteUrls = useCallback((): SiteUrls => ({
    transSourceUrl: getBaseUrl(),
  }), [getBaseUrl]);

  return {
    getSiteId,
    getSiteData,
    getBaseUrl,
    getSiteUrls,
  };
}
